import {useGoogleService} from '@/composables'
import {useAppStore, useUserStore} from '@/stores'
import {useAsyncState} from '@vueuse/core'
import axios from 'axios'
import {defineStore} from 'pinia'
import {watch} from 'vue'

interface UserLocation {
  latitude: number
  longitude: number
  accuracy: number
  address?: any
  source: 'google' | 'browser'
  denied?: boolean
}

export default defineStore('location', () => {
  const app = useAppStore() as any
  const user = useUserStore() as any
  const settings: PositionOptions = {
    enableHighAccuracy: true,
    timeout: 10000,
    maximumAge: 0,
  }
  const googleApiEndpoint =
    'https://www.googleapis.com/geolocation/v1/geolocate?key=AIzaSyBHuCioOrHD5vHTdnnhzWTQiikVk6Rgp8c'

  async function getGoogleLocation(): Promise<UserLocation | undefined> {
    const response = await axios.post(
      googleApiEndpoint,
      {},
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    )
    if (response.status >= 200 && response.status < 400) {
      const {data} = response
      const {
        accuracy,
        location: {lat: latitude, lng: longitude},
      } = data
      try {
        const {getAddressUsingCoordinates} = useGoogleService()
        const address = await getAddressUsingCoordinates(latitude, longitude)
        return {latitude, longitude, accuracy, address, source: 'google'}
      } catch (error) {
        return {latitude, longitude, accuracy, source: 'google'}
      }
    }
  }

  async function getBrowserLocation(): Promise<UserLocation> {
    const {getAddressUsingCoordinates} = useGoogleService()
    return new Promise((resolve, reject) => {
      const watcher = navigator.geolocation.watchPosition(
        async position => {
          navigator.geolocation.clearWatch(watcher)
          const {latitude, longitude, accuracy} = position.coords
          try {
            const address = await getAddressUsingCoordinates(latitude, longitude)
            resolve({latitude, longitude, accuracy, address, source: 'browser'})
          } catch (error) {
            resolve({latitude, longitude, accuracy, source: 'browser'})
          }
        },
        error => {
          reject(error)
        },
        settings
      )
    })
  }

  async function getLocation(): Promise<UserLocation> {
    try {
      return await getBrowserLocation()
    } catch (error: any) {
      // GeolocationPositionError code 1 is PERMISSION_DENIED; 2 (unavailable) and
      // 3 (timeout) are not permission problems, so the IP-based fallback location
      // must not be reported as a denied permission.
      const denied = error?.code === 1
      const location = await getGoogleLocation()
      if (!location) throw error
      return {...location, denied}
    }
  }

  // Location is never blocking: errors stay in `error` and the user store keeps no location
  const {
    state: location,
    isLoading: locatingUser,
    error: locationError,
    execute: requestLocation,
  } = useAsyncState<UserLocation | null>(getLocation, null)
  watch(location, value => {
    if (value) user.setLocation(value)
  })

  return {
    getLocation,
    location,
    locatingUser,
    locationError,
    requestLocation,
  }
})
